<template>
  <div data-app>
    <div
      role="alert"
      v-bind:class="{ show: errors.length }"
      class="alert fade alert-danger"
    >
      <div class="alert-text" v-for="(error, i) in errors" :key="i">
        {{ error }}
      </div>
    </div>
    <v-card>
      <v-card-title>{{ website_name }} Page Security</v-card-title>
      <v-card-text>
        <v-dialog v-model="globalSettingsDialog" max-width="600px">
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              color="primary"
              dark
              v-bind="attrs"
              v-on="on"
              class="mr-2 mb-2"
            >
              <v-icon left>mdi-cog</v-icon>
              Edit Page Security Settings
            </v-btn>
            <v-btn color="primary" dark class="mb-2" @click="exportToCsv">
              <v-icon left>mdi-download</v-icon>
              Download CSV
            </v-btn>
          </template>
          <v-card>
            <v-card-title>Page Security Settings</v-card-title>
            <v-card-text>
              <v-container>
                <v-row dense>
                  <v-col cols="12">
                    <v-checkbox
                      v-model="settings.enabled"
                      label="Enable Page Security"
                    />
                  </v-col>

                  <v-col cols="12">
                    <v-subheader>Notifications</v-subheader>
                    <v-text-field
                      v-model="settings.notify.email"
                      label="Email Address"
                      :rules="[
                        (v) =>
                          !v || /.+@.+\..+/.test(v) || 'Email must be valid',
                      ]"
                    />
                    <v-text-field
                      v-model="settings.notify.webhook"
                      label="Webhook URL"
                      :rules="[
                        (v) =>
                          !v ||
                          /^https?:\/\/.+/.test(v) ||
                          'Must be a valid URL',
                      ]"
                    />
                  </v-col>
                </v-row>
              </v-container>
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn
                color="blue darken-1"
                text
                @click="globalSettingsDialog = false"
                >Cancel</v-btn
              >
              <v-btn color="blue darken-1" text @click="saveSettings"
                >Save</v-btn
              >
            </v-card-actions>
          </v-card>
        </v-dialog>
      </v-card-text>
      <v-data-table
        v-model="selected"
        :loading="loading"
        :headers="headers"
        :items="policies"
        :footer-props="{
          itemsPerPageOptions: [10, 20, 50, 100],
          showFirstLastPage: true,
          firstIcon: 'mdi-arrow-collapse-left',
          lastIcon: 'mdi-arrow-collapse-right',
          prevIcon: 'mdi-chevron-left',
          nextIcon: 'mdi-chevron-right',
        }"
        :items-per-page="itemsPerPage"
        :server-items-length="totalPolicies"
        @update:page="onPageChange"
        @update:items-per-page="onItemsPerPageChange"
        item-key="id"
        class="elevation-1"
      >
        <template v-slot:top>
          <v-toolbar flat color="white">
            <v-text-field
              v-model="search"
              append-icon="mdi-magnify"
              label="Search"
              single-line
              hide-details
            ></v-text-field>
            <v-spacer></v-spacer>
            <v-dialog v-model="dialog" max-width="500px">
              <template v-slot:activator="{ on }">
                <v-btn color="primary" dark class="mb-2" v-on="on"
                  >New Policy</v-btn
                >
              </template>
              <v-card>
                <v-card-title>
                  <span class="headline">{{ formTitle }}</span>
                </v-card-title>

                <v-card-text>
                  <v-form ref="newForm" lazy-validation>
                    <v-container>
                      <v-row dense>
                        <v-col cols="12">
                          <v-text-field
                            v-model="editedItem.policy"
                            label="Policy"
                          />
                        </v-col>
                        <div class="v-text-field__slot">
                          <v-label
                            style="left: 0px; right: auto; position: absolute"
                            >Paths</v-label
                          >
                          <vue-tags-input
                            v-model="path"
                            :tags="editedItem.paths"
                            placeholder="Add paths"
                            @tags-changed="updatePaths"
                          />
                        </div>
                        <v-col cols="12">
                          <v-checkbox
                            v-model="editedItem.enabled"
                            label="Enabled"
                          />
                        </v-col>
                        <v-col cols="12">
                          <v-checkbox
                            v-model="editedItem.report_only"
                            label="Report Only"
                          />
                        </v-col>
                      </v-row>
                    </v-container>
                  </v-form>
                </v-card-text>

                <v-card-actions>
                  <v-spacer></v-spacer>
                  <v-btn color="blue darken-1" text @click="close"
                    >Cancel</v-btn
                  >
                  <v-btn color="blue darken-1" text @click="save">Save</v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
          </v-toolbar>
        </template>
        <template v-slot:item.actions="{ item }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <v-icon small class="mr-2" @click="deleteItem(item)" v-on="on"
                >mdi-delete</v-icon
              >
            </template>
            <span>Delete</span>
          </v-tooltip>
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <v-icon small class="mr-2" @click="editItem(item)" v-on="on"
                >mdi-pencil</v-icon
              >
            </template>
            <span>Edit</span>
          </v-tooltip>
        </template>
      </v-data-table>
    </v-card>
  </div>
</template>

<script>
import api from "@/common/api.service";
import Errors from "@/common/errors";
import VueTagsInput from "@johmun/vue-tags-input";
import { jsonToCsv, downloadCsv } from "@/common/json-to-csv";

export default {
  mounted() {
    this.fetchPolicies();
    this.fetchSettings();
    this.website_name = this.$store.state.web.website_name;
  },

  components: {
    VueTagsInput,
  },

  data: () => ({
    path: "",
    selected: [],
    headers: [
      { text: "ID", sortable: false, align: "left", value: "id" },
      { text: "Policy", value: "policy" },
      { text: "Paths", value: "paths" },
      { text: "Enabled", value: "enabled" },
      { text: "Report Only", value: "report_only" },
      { text: "Actions", value: "actions", sortable: false },
    ],
    policies: [],
    settings: {
      enabled: false,
      notify: {
        email: "",
        webhook: "",
      },
    },
    errors: [],
    loading: true,
    dialog: false,
    editedItem: {
      website_id: "",
      policy: "",
      paths: [],
      enabled: true,
      report_only: false,
    },
    defaultItem: {
      website_id: "",
      policy: "",
      paths: [],
      enabled: true,
      report_only: false,
    },
    search: "",
    websites: null,
    required: [(v) => !!v || "Value is required"],
    website_name: "",
    editedIndex: -1,
    itemsPerPage: 10,
    totalPolicies: 0,
    currentPage: 1,
    searchCounter: 0,
    globalSettingsDialog: false,
    globalSettings: {
      enabled: false,
      auto_prefixes: false,
      exclude_prefixes: [],
      notify: {
        email: "",
        webhook: "",
      },
    },
  }),

  watch: {
    dialog(val) {
      val || this.close();
    },
  },

  methods: {
    editItem(item) {
      this.editedIndex = this.policies.indexOf(item);
      this.editedItem = Object.assign({}, item);
      this.dialog = true;
    },

    fetchPolicies() {
      this.searchCounter++;
      const currentRequest = this.searchCounter;

      this.loading = true;
      const queryParams = new URLSearchParams();
      queryParams.append("page", this.currentPage);
      queryParams.append("size", this.itemsPerPage);
      queryParams.append("customer_website_id", this.$route.params.website_id);

      api
        .query(`v2/waf/page_security/policies?${queryParams.toString()}`)
        .then((response) => {
          if (this.searchCounter == currentRequest) {
            this.policies = response.data.data;
            this.totalPolicies = response.data.total;
          }
        })
        .catch((error) =>
          this.errors.push(...Errors.extractErrorMessage(error))
        )
        .finally(() => {
          this.loading = false;
        });
    },

    onPageChange(page) {
      this.currentPage = page;
      this.fetchPolicies();
    },

    onItemsPerPageChange(size) {
      this.itemsPerPage = size;
      this.currentPage = 1; // Reset to the first page
      this.fetchPolicies();
    },

    fetchSettings() {
      api
        .query(`v2/waf/page_security/settings/${this.$route.params.website_id}`)
        .then((response) => {
          this.settings = response.data;
        })
        .catch((error) =>
          this.errors.push(...Errors.extractErrorMessage(error))
        );
    },

    updatePaths(newPaths) {
      this.editedItem.paths = newPaths.map((path) => path.text);
    },

    deleteItem(item) {
      if (confirm("Are you sure you want to delete this item?")) {
        api
          .delete("beta/waf/page_security/policies/" + item.id)
          .then((response) => {
            const index = this.policies.indexOf(item);
            this.policies.splice(index, 1);
          });
      }
    },

    close() {
      this.dialog = false;
      setTimeout(() => {
        this.editedItem = Object.assign({}, this.defaultItem);
        this.editedIndex = -1;
      }, 300);
    },

    save() {
      if (!this.$refs.newForm.validate()) return;

      let newPolicy = {
        customer_website_id: this.$route.params.website_id,
        policy: this.editedItem.policy,
        paths: this.editedItem.paths,
        enabled: this.editedItem.enabled,
        report_only: this.editedItem.report_only,
      };

      api
        .post("v2/waf/page_security/policies", newPolicy)
        .then((response) => {
          this.policies = [response.data, ...this.policies];
        })
        .catch((error) =>
          this.errors.push(...Errors.extractErrorMessage(error))
        );

      this.close();
    },

    saveSettings() {
      api
        .patch(
          `v2/waf/page_security/settings/${this.$route.params.website_id}`,
          this.settings
        )
        .then((response) => {
          this.settings = response.data;
        })
        .catch((error) =>
          this.errors.push(...Errors.extractErrorMessage(error))
        );

      this.globalSettingsDialog = false;
    },

    async exportToCsv() {
      try {
        const totalPages = Math.ceil(this.totalPolicies / 100);
        const promises = [];

        // Create array of promises for all API calls
        for (let page = 1; page <= totalPages; page++) {
          const queryParams = new URLSearchParams();
          queryParams.append("page", page);
          queryParams.append("size", 100);
          queryParams.append(
            "customer_website_id",
            this.$route.params.website_id
          );

          promises.push(
            api.query(`v2/waf/page_security/policies?${queryParams.toString()}`)
          );
        }

        // Wait for all API calls to complete
        const responses = await Promise.all(promises);

        // Process all the responses
        for (const response of responses) {
          this.policies = [...this.policies, ...response.data.data];
        }

        const csv = jsonToCsv(this.policies);
        downloadCsv(csv, this.website_name + "_policies.csv");
      } catch (error) {
        console.error("Error exporting to CSV:", error);
        this.errors.push("Failed to export data to CSV");
      }
    },
  },

  computed: {
    formTitle() {
      return this.editedIndex === -1 ? "New Policy" : "Edit Policy";
    },
  },
};
</script>
